import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const typeLabels = {
  url: 'URL',
  email: 'Email',
  phone: 'Phone',
  crypto_wallet: 'Crypto Wallet',
  iban: 'IBAN',
  domain: 'Domain',
};

const typeColors = {
  url: 'text-blue-400',
  email: 'text-purple-400',
  phone: 'text-yellow-400',
  crypto_wallet: 'text-orange-400',
  iban: 'text-pink-400',
  domain: 'text-cyan-400',
};

function CopyButton({ value }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={t('entities.copyValue')}
      className="relative flex-shrink-0 p-1 rounded hover:bg-dark-border/30 transition-colors group"
    >
      {copied ? (
        <span className="text-safe text-xs font-medium whitespace-nowrap">{t('common.copied')}</span>
      ) : (
        <svg
          className="w-3.5 h-3.5 text-gray-500 group-hover:text-gray-300 transition-colors"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184"
          />
        </svg>
      )}
    </button>
  );
}

function EntityRow({ entity, index, expandedId, setExpandedId }) {
  const { t } = useTranslation();
  const hasDetails = entity.check_result && Object.keys(entity.check_result).length > 0;
  const isExpanded = expandedId === index;

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpandedId(isExpanded ? null : index)}
        aria-expanded={hasDetails ? isExpanded : undefined}
        className="w-full flex items-center gap-3 py-2 px-2 rounded hover:bg-dark-surface/50 transition-colors text-left border-b border-dark-border/30 last:border-0"
      >
        <span className={`text-xs font-mono uppercase w-16 flex-shrink-0 ${typeColors[entity.entity_type] || 'text-gray-400'}`}>
          {typeLabels[entity.entity_type] || entity.entity_type}
        </span>
        <span className="text-gray-300 text-sm font-mono truncate flex-1">
          {entity.value}
        </span>
        <CopyButton value={entity.value} />
        {entity.is_malicious === true && (
          <span className="bg-danger/20 text-danger text-xs px-2 py-0.5 rounded flex-shrink-0">{t('entities.malicious')}</span>
        )}
        {entity.is_malicious === false && (
          <span className="bg-safe/20 text-safe text-xs px-2 py-0.5 rounded flex-shrink-0">{t('entities.clean')}</span>
        )}
        {hasDetails && (
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform duration-200 flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        )}
      </button>
      {isExpanded && hasDetails && (
        <div className="ml-20 mb-2 p-3 bg-dark-surface/50 rounded-lg text-xs">
          {Object.entries(entity.check_result)
            .filter(([, v]) => v !== null && v !== undefined && v !== '' && typeof v !== 'object')
            .map(([key, val]) => (
              <div key={key} className="flex justify-between py-0.5">
                <span className="text-gray-500">{key}</span>
                <span className="text-gray-300 font-mono">{String(typeof val === 'boolean' ? (val ? 'Oui' : 'Non') : val)}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export default function EntityList({ entities, embedded }) {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState(null);

  if (!entities || entities.length === 0) return null;

  const malicious = [];
  const other = [];
  entities.forEach((entity, i) => {
    const item = { entity, originalIndex: i };
    if (entity.is_malicious === true) {
      malicious.push(item);
    } else {
      other.push(item);
    }
  });

  const content = (
    <div className="space-y-1 max-h-80 overflow-y-auto">
      {malicious.length > 0 && (
        <div className="border-l-2 border-danger/60 pl-2">
          <div className="text-xs font-semibold text-danger/80 uppercase tracking-wide py-1 px-1">
            {t('entities.suspiciousElements')}
          </div>
          {malicious.map(({ entity, originalIndex }) => (
            <EntityRow
              key={originalIndex}
              entity={entity}
              index={originalIndex}
              expandedId={expandedId}
              setExpandedId={setExpandedId}
            />
          ))}
        </div>
      )}
      {malicious.length > 0 && other.length > 0 && (
        <div className="border-t border-dark-border/40 my-2" />
      )}
      {other.map(({ entity, originalIndex }) => (
        <EntityRow
          key={originalIndex}
          entity={entity}
          index={originalIndex}
          expandedId={expandedId}
          setExpandedId={setExpandedId}
        />
      ))}
    </div>
  );

  if (embedded) return content;

  return (
    <div className="glass-card p-6">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <svg className="w-5 h-5 text-safe" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v16.5c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
        {t('entities.title')} ({entities.length})
      </h3>
      {content}
    </div>
  );
}
